import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  ADJUSTMENT_KIND_LABELS,
  LeaveBalanceService,
  ADJUSTMENT_KINDS,
  isCreditKind,
  resolveAdjustmentKind,
  signedAdjustmentDays,
} from '../../../services/leaveBalanceService';
import { LeaveAuditService } from '../../../services/leaveAuditService';
import { LeavePolicyService, LeaveSettings } from '../../../services/leavePolicyService';
import { employeeChangeDeadline } from '../../../services/leaveVisibility';
import { resolveActorId, sameIdentity } from '../../../services/identity';
import { LeaveAdjustmentKind, LeaveAuditLog } from '../../../types/leavePolicy';
import { UserRole } from '../../../types/user';
import { loadAllUsers, matchesActorId, SimpleUser } from '../../../services/userLoader';
import { LeavePageHeader, YearSelect } from './leaveShared';
import '../LeavePage.css';
import './leavePages.css';
import '../../common/emsPage.css';

type OpTab = 'ADJUST' | 'BULK' | 'ROLLOVER' | 'SETTINGS' | 'AUDIT';

const HROperationsPage: React.FC = () => {
  const { user } = useAuth();
  /*
   * The identity recorded against everything HR does here.
   *
   * Was `user?.email || ''`, and a federated Azure AD admin account can have no email at all —
   * so every adjustment was written with an empty actor and the audit trail's "Changed By"
   * column rendered as "—", with no way to tell who had granted the days.
   */
  const userEmail = resolveActorId(user);

  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState<OpTab>('ADJUST');
  const [targetEmail, setTargetEmail] = useState<string>('');
  const [targetSearch, setTargetSearch] = useState<string>('');
  const [showTargetDropdown, setShowTargetDropdown] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState(() => LeavePolicyService.getLeaveTypes().filter(t => t.isActive));
  const [leaveTypeCode, setLeaveTypeCode] = useState<string>('EL');
  // A raw string buffer, not a number: a controlled <input type="number"> that converts
  // via Number(e.target.value) on every keystroke snaps back to "0" the instant someone
  // types a lone "-" (the DOM reports "" for an incomplete numeric value), which silently
  // discards the minus sign and makes negative adjustments impossible to type. Validated
  // and parsed once, at submit time, in handleAdjustmentSubmit.
  const [adjustmentDaysInput, setAdjustmentDaysInput] = useState<string>('1');
  const [reason, setReason] = useState<string>('');
  /** Optional expiry on a granted balance. Blank means the days never lapse. */
  const [grantExpiresOn, setGrantExpiresOn] = useState<string>('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [auditLogs, setAuditLogs] = useState<LeaveAuditLog[]>([]);
  const [settingsDraft, setSettingsDraft] = useState<LeaveSettings>(() => LeavePolicyService.getSettings());

  // Bulk Grant / Deduction state
  const [bulkEmployees, setBulkEmployees] = useState<SimpleUser[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  /*
   * Which side of the balance an adjustment lands on — entitlement, or leave already taken.
   *
   * Held per form, and defaulted to the behaviour these forms had before the field existed: a
   * credit adds entitlement, a deduction records leave taken before go-live. So an HR user who
   * ignores the control writes exactly what they wrote yesterday.
   */
  const [adjustmentKind, setAdjustmentKind] = useState<LeaveAdjustmentKind | ''>('');
  /**
   * The kind in force — the chosen one, or the credit default before anything is chosen.
   *
   * Named separately from the state because the direction of the adjustment is now read off it in
   * three places (the days label, the expiry field's visibility, and the signed value stored), and
   * each recomputing its own default is how they would drift apart.
   */
  const [bulkKind, setBulkKind] = useState<LeaveAdjustmentKind | ''>('');
  const effectiveKind: LeaveAdjustmentKind = adjustmentKind || 'ENTITLEMENT_CREDIT';
  const effectiveBulkKind: LeaveAdjustmentKind = bulkKind || 'ENTITLEMENT_CREDIT';

  const [bulkType, setBulkType] = useState('EL');
  const [bulkDays, setBulkDays] = useState(1);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const refreshAudit = useCallback(() => setAuditLogs(LeaveAuditService.getAuditLogs()), []);

  /*
   * Pull the trail from the server, then read the cache.
   *
   * refreshAudit alone only re-reads this browser's cache, which is why Audit History used to
   * show nothing but the viewer's own actions — the entries other people created were never
   * fetched. The 'leave-audit-updated' listener keeps the table current as entries are written
   * elsewhere in the session.
   */
  useEffect(() => {
    let active = true;
    const pull = async () => {
      await LeaveAuditService.refresh();
      if (active) refreshAudit();
    };
    pull();
    window.addEventListener('leave-audit-updated', refreshAudit);
    return () => {
      active = false;
      window.removeEventListener('leave-audit-updated', refreshAudit);
    };
  }, [refreshAudit]);

  // Employee directory — needed by the target-employee search (ADJUST) as well
  // as the bulk employee picker (BULK), so it's loaded once regardless of active tab.
  useEffect(() => {
    if (bulkEmployees.length > 0) return;
    setBulkLoading(true);
    loadAllUsers()
      .then(setBulkEmployees)
      .catch(() => setBulkEmployees([]))
      .finally(() => setBulkLoading(false));
  }, [bulkEmployees.length]);

  // Leave types are user-configurable (Leave Policies > Leave Types) — refresh live so a
  // newly created/edited/deactivated type shows up here without a full page reload.
  useEffect(() => {
    const refresh = () => setLeaveTypes(LeavePolicyService.getLeaveTypes().filter(t => t.isActive));
    window.addEventListener('leave-policy-updated', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('leave-policy-updated', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const bulkVisible = useMemo(() => {
    const term = bulkSearch.trim().toLowerCase();
    if (!term) return bulkEmployees;
    return bulkEmployees.filter((e) => (e.fullName || e.name).toLowerCase().includes(term) || e.email.toLowerCase().includes(term));
  }, [bulkEmployees, bulkSearch]);

  const targetVisible = useMemo(() => {
    const term = targetSearch.trim().toLowerCase();
    if (!term) return bulkEmployees.slice(0, 20);
    return bulkEmployees
      .filter((e) => (e.fullName || e.name).toLowerCase().includes(term) || e.email.toLowerCase().includes(term))
      .slice(0, 20);
  }, [bulkEmployees, targetSearch]);

  const selectTargetEmployee = (e: SimpleUser) => {
    setTargetEmail(e.email);
    setTargetSearch(`${e.fullName || e.name} (${e.email})`);
    setShowTargetDropdown(false);
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3200);
  };

  /** Resolves an email to a display name via the already-loaded employee directory,
   * falling back to the email itself so the audit trail never shows a blank cell. */
  const nameForEmail = useCallback((email?: string): string => {
    if (!email) return '—';
    // sameIdentity: a person's two domain accounts are one person, so an audit row written
    // against either address must still resolve to their name rather than falling back to the
    // raw email.
    const match = bulkEmployees.find((e) => sameIdentity(e.email, email));
    return match ? (match.fullName || match.name) : email;
  }, [bulkEmployees]);

  /** Human-readable role for the "Changed By" column. */
  const ROLE_LABELS: Record<string, string> = {
    [UserRole.SUPER_ADMIN]: 'Super Admin',
    [UserRole.HR_ADMIN]: 'HR',
    [UserRole.FINANCE_ADMIN]: 'Finance Admin',
    [UserRole.MANAGER]: 'Manager',
    [UserRole.EMPLOYEE]: 'Employee',
  };

  /**
   * Who made the change, as a name plus their role.
   *
   * The audit trail stored only an email — and for federated SSO accounts not even
   * that, just an opaque `azuread_…` username — so the column read as a technical
   * id rather than telling anyone who actually acted. Falls back to the raw value
   * only when the directory has no match, so nothing is ever blank.
   */
  const actorFor = useCallback((actorId?: string): { name: string; email: string; role: string | null } => {
    // "Not recorded" rather than "Unknown": the entry was written with no actor at all, which
    // is a different fact from a person the directory cannot place. Rows predating
    // resolveActorId carry an empty actor and cannot be attributed retrospectively.
    if (!actorId) return { name: 'Not recorded', email: '', role: null };
    /*
     * Matched against every identifier the directory holds, not email alone — see
     * matchesActorId, which owns the rule.
     *
     * resolveActorId records `user.email || user.id`, so an account whose Cognito email
     * attribute is unset is written to the trail as its raw sub. That used to match nothing
     * here, leaving the Changed By column showing "14e8b488-4021-701f-d87d-fa053350afde"
     * instead of a person — because the session's id is the Cognito *sub* while the
     * directory's `id` is the Cognito *username*, and for a federated account those are
     * never the same string. Carrying the sub through the directory is what made the
     * comparison possible; the lookup happens at render time, so rows already written
     * resolve too.
     */
    const match = bulkEmployees.find((e) => matchesActorId(e, actorId));
    // The raw id is carried through as `email` even when the directory has no match: it is
    // what was actually recorded, and for a federated account it is the only identifier there
    // is. Showing the name alone hid it, which is what made "who changed this" unanswerable.
    if (!match) return { name: actorId, email: actorId, role: null };
    return {
      name: match.fullName || match.name,
      email: match.email,
      role: ROLE_LABELS[match.role] || match.role || null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkEmployees]);

  const ADJUSTMENT_STEP = 0.5;
  const adjustDaysBy = (delta: number) => {
    setAdjustmentDaysInput((prev) => {
      const current = Number(prev);
      const base = Number.isFinite(current) ? current : 0;
      return String(Math.round((base + delta) * 100) / 100);
    });
  };

  const toggleBulk = (email: string) => setBulkSelected((prev) => {
    const next = new Set(prev); next.has(email) ? next.delete(email) : next.add(email); return next;
  });

  const handleBulkApply = async () => {
    if (bulkSelected.size === 0) { showToast('Select at least one employee.', 'error'); return; }
    if (!bulkReason.trim()) { showToast('Reason is required.', 'error'); return; }
    if (bulkDays <= 0) { showToast('Days must be greater than 0.', 'error'); return; }
    // Direction from the kind, exactly as the single form does it — the two must not disagree
    // about what "Entitlement reduced" means.
    const kind = resolveAdjustmentKind(isCreditKind(effectiveBulkKind), effectiveBulkKind);
    const signedDays = signedAdjustmentDays(kind, bulkDays);
    setBulkBusy(true);
    try {
      // Awaited, and the per-employee outcome checked: these are server writes now, so a
      // blanket "completed successfully" fired before they landed would tell HR the team's
      // balances had moved when some may not have.
      const results = await Promise.all(
        Array.from(bulkSelected).map((email) =>
          LeaveBalanceService.addAdjustment(
            email, bulkType, year, signedDays, userEmail,
            // The kind's own label, so the reason on the trail names the same thing the
            // adjustment was recorded as rather than a second vocabulary for it.
            `Bulk ${ADJUSTMENT_KIND_LABELS[kind].toLowerCase()}: ${bulkReason.trim()}`,
            undefined,
            kind
          )
        )
      );
      const failed = results.filter((r) => !r.ok).length;
      if (failed > 0) {
        showToast(`${results.length - failed} of ${results.length} saved — ${failed} could not reach the server.`, 'error');
      } else {
        showToast('Leave adjustment completed successfully.', 'success');
        setBulkSelected(new Set());
        setBulkReason('');
      }
      refreshAudit();
    } finally {
      setBulkBusy(false);
    }
  };

  const handleAdjustmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const typed = Number(adjustmentDaysInput);
    if (adjustmentDaysInput.trim() === '' || Number.isNaN(typed) || typed === 0) {
      showToast('Enter a number of days (e.g. 2 or 1.5).', 'error');
      return;
    }
    /*
     * The direction comes from the kind, never from the sign of what was typed.
     *
     * Both used to be collected, and the sign won: entering 28 with "Entitlement reduced" chosen
     * stored a credit, so an HR user reducing an entitlement by 28 watched it grow by 28. The
     * field is a magnitude now, and a stray minus in it is ignored rather than fought over.
     */
    const kind = resolveAdjustmentKind(isCreditKind(effectiveKind), effectiveKind);
    const days = signedAdjustmentDays(kind, typed);
    // `ok` distinguishes "the employee will see this" from "cached on this machine only",
    // which is the difference that made a granted balance silently never arrive.
    const { ok } = await LeaveBalanceService.addAdjustment(
      targetEmail, leaveTypeCode, year, days, userEmail, reason, grantExpiresOn || undefined, kind
    );
    showToast(
      ok
        ? 'Leave adjustment saved — the employee’s balance is updated.'
        : 'Saved on this device only; the server could not be reached, so the employee will not see it yet.',
      ok ? 'success' : 'error'
    );
    refreshAudit();
  };

  const handleRolloverSubmit = async () => {
    if (window.confirm(`Are you sure you want to execute Dec 31 Year-End processing for year ${year}? Balances reset for ${year + 1} (no carry-forward — unused leave lapses).`)) {
      await LeaveBalanceService.performYearEndRollover(year, userEmail);
      showToast(`Year-End Rollover from ${year} to ${year + 1} completed successfully.`, 'success');
      refreshAudit();
    }
  };


  const TABS: { key: OpTab; label: string }[] = [
    { key: 'ADJUST', label: 'Grant Leave' },
    { key: 'BULK', label: 'Bulk Grant / Deduct' },
    { key: 'ROLLOVER', label: 'Year-End Rollover' },
    { key: 'SETTINGS', label: 'Leave Settings' },
    { key: 'AUDIT', label: 'Audit History' },
  ];

  return (
    <section className="leave-page">
      <LeavePageHeader title="HR Leave Operations" />

      {toast && <div className={`ems-toast ems-toast--${toast.type}`}>{toast.message}</div>}

      {/* Flat tab strip matching .dashboard-tabs in the EMS Dashboard: no segmented
          container, a 3px underline on the active tab, equal spacing. */}
      <nav className="ems-tabs" role="tablist" aria-label="HR leave operations sections">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeTab === t.key}
            className={`ems-tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => { setActiveTab(t.key); if (t.key === 'AUDIT') refreshAudit(); }}
          >
            {t.label}
            {t.key === 'AUDIT' && auditLogs.length > 0 && (
              <span className="ems-tab-count">{auditLogs.length}</span>
            )}
          </button>
        ))}
      </nav>

      {/* Action bar between tabs and content, left aligned, right side left free for
          search or filters later. */}
      <div className="ems-actionbar">
        <YearSelect value={year} onChange={setYear} />
      </div>

      {activeTab === 'ADJUST' && (
        <form onSubmit={handleAdjustmentSubmit} className="ems-card">
          <div className="ems-card-head">
            <div>
              <h3>Grant / Adjust Leave</h3>
              <p className="ems-card-sub">
                Credit or debit one employee's balance. Every change is recorded in the audit trail.
              </p>
            </div>
          </div>

          <div className="ems-form-grid">
            <div className="ems-field ems-field--wide lb-employee-search-field">
              <label htmlFor="hr-target-email">Target Employee</label>
              <input
                id="hr-target-email"
                type="text"
                autoComplete="off"
                placeholder="Search by name or email"
                value={targetSearch}
                onChange={(e) => { setTargetSearch(e.target.value); setTargetEmail(e.target.value); setShowTargetDropdown(true); }}
                onFocus={() => setShowTargetDropdown(true)}
                onBlur={() => setTimeout(() => setShowTargetDropdown(false), 150)}
                required
              />
              {showTargetDropdown && (
                <div className="lb-employee-search-dropdown">
                  {targetVisible.length === 0 ? (
                    <div className="lb-employee-search-empty">No employees match your search.</div>
                  ) : (
                    targetVisible.map((e) => (
                      <button
                        type="button"
                        key={e.email}
                        className="lb-employee-search-item"
                        onMouseDown={(evt) => evt.preventDefault()}
                        onClick={() => selectTargetEmployee(e)}
                      >
                        <span>{e.fullName || e.name}</span>
                        <span className="lb-employee-search-item-email">{e.email}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <div className="ems-field">
              <label htmlFor="hr-leave-type">Leave Type</label>
              <select id="hr-leave-type" value={leaveTypeCode} onChange={(e) => setLeaveTypeCode(e.target.value)}>
                {leaveTypes.map((t) => (
                  <option key={t.code} value={t.code}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="ems-field">
              {/* No "+ or −": the direction is the Records as control's job now. */}
              <label htmlFor="hr-adj-days">Days</label>
              <div className="ems-num-stepper">
                <input
                  id="hr-adj-days"
                  type="text"
                  inputMode="decimal"
                  value={adjustmentDaysInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    // Gate to partial-or-complete signed decimals so every other keystroke
                    // is rejected outright, rather than parsed and silently coerced.
                    if (v === '' || v === '-' || /^-?\d*\.?\d*$/.test(v)) setAdjustmentDaysInput(v);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp') { e.preventDefault(); adjustDaysBy(ADJUSTMENT_STEP); }
                    else if (e.key === 'ArrowDown') { e.preventDefault(); adjustDaysBy(-ADJUSTMENT_STEP); }
                  }}
                  placeholder="e.g. 2 or 1.5"
                  required
                />
                <span className="ems-num-stepper-buttons">
                  <button type="button" tabIndex={-1} onClick={() => adjustDaysBy(ADJUSTMENT_STEP)} aria-label="Increase">▲</button>
                  <button type="button" tabIndex={-1} onClick={() => adjustDaysBy(-ADJUSTMENT_STEP)} aria-label="Decrease">▼</button>
                </span>
              </div>
            </div>
            {/* All four, always. Filtering them by the sign of the days field is what let a
                reduction be stored as a credit — see handleAdjustmentSubmit. */}
            <div className="ems-field">
              <label htmlFor="hr-adj-kind">Type of change</label>
              <select
                id="hr-adj-kind"
                value={effectiveKind}
                onChange={(e) => setAdjustmentKind(e.target.value as LeaveAdjustmentKind)}
              >
                {ADJUSTMENT_KINDS.map((k) => (
                  <option key={k} value={k}>{ADJUSTMENT_KIND_LABELS[k]}</option>
                ))}
              </select>
              <span className="ems-field-hint">
                {/* States the direction outright, because this control is now the only thing that
                    decides it — the days field no longer carries a sign. */}
                {isCreditKind(effectiveKind)
                  ? `Adds ${adjustmentDaysInput.trim() ? Math.abs(Number(adjustmentDaysInput)) || '' : ''} day(s) to the balance.`
                  : `Takes ${adjustmentDaysInput.trim() ? Math.abs(Number(adjustmentDaysInput)) || '' : ''} day(s) off the balance.`}
                {' '}
                {effectiveKind === 'ENTITLEMENT_CREDIT' || effectiveKind === 'ENTITLEMENT_REDUCTION'
                  ? 'Changes the employee’s leave allotment for this year.'
                  : 'Changes the days already taken; the allotment stays the same.'}
              </span>
            </div>
            <div className="ems-field">
              <label htmlFor="hr-expires">Expires on <span style={{ fontWeight: 400, color: '#64748b' }}>(optional)</span></label>
              <input
                id="hr-expires"
                type="date"
                value={grantExpiresOn}
                onChange={(e) => setGrantExpiresOn(e.target.value)}
              />
              <span className="ems-field-hint">
                Last day the granted days may be used. Leave blank and they never lapse.
              </span>
            </div>
            <div className="ems-field ems-field--wide">
              <label htmlFor="hr-reason">Reason / Audit Note</label>
              <input id="hr-reason" type="text" value={reason} onChange={(e) => setReason(e.target.value)} required />
            </div>
          </div>

          <div className="ems-form-actions">
            <button type="submit" className="ems-btn-primary">Apply Adjustment</button>
          </div>
        </form>
      )}

      {activeTab === 'BULK' && (
        <>
          <div className="ems-card">
            <div className="ems-card-head">
              <div>
                <h3>Bulk Grant / Deduction</h3>
                <p className="ems-card-sub">
                  Apply a credit or debit to many employees at once. Each change is audited separately.
                </p>
              </div>
            </div>

            <div className="ems-form-grid">
              {/*
                No separate Grant/Deduct control any more.
                It said the same thing as Records as and the two could contradict each other, which
                is how a reduction ended up stored as a credit. One control, one direction.
              */}
              <div className="ems-field">
                <label htmlFor="hr-bulk-kind">Type of change</label>
                <select
                  id="hr-bulk-kind"
                  value={effectiveBulkKind}
                  onChange={(e) => setBulkKind(e.target.value as LeaveAdjustmentKind)}
                >
                  {ADJUSTMENT_KINDS.map((k) => (
                    <option key={k} value={k}>{ADJUSTMENT_KIND_LABELS[k]}</option>
                  ))}
                </select>
                <span className="ems-field-hint">
                  {isCreditKind(effectiveBulkKind)
                    ? 'Adds days to every selected employee.'
                    : 'Takes days off every selected employee.'}
                </span>
              </div>
              <div className="ems-field">
                <label htmlFor="hr-bulk-type">Leave Type</label>
                <select id="hr-bulk-type" value={bulkType} onChange={(e) => setBulkType(e.target.value)}>
                  {leaveTypes.map((t) => (
                    <option key={t.code} value={t.code}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="ems-field">
                <label htmlFor="hr-bulk-days">Days</label>
                <input id="hr-bulk-days" type="number" min={0.5} step={0.5} value={bulkDays} onChange={(e) => setBulkDays(Number(e.target.value))} />
              </div>
              <div className="ems-field ems-field--wide">
                <label htmlFor="hr-bulk-reason">Reason / Audit Note</label>
                <input id="hr-bulk-reason" type="text" value={bulkReason} placeholder="e.g. Annual EL top-up for engineering" onChange={(e) => setBulkReason(e.target.value)} required />
              </div>
            </div>
          </div>

          <div className="ems-card">
            <div className="ems-card-head">
              <div>
                <h3>Select Employees</h3>
                <p className="ems-card-sub">Choose who the adjustment applies to.</p>
              </div>
              <span className={bulkSelected.size > 0 ? 'ems-badge ems-badge--on' : 'ems-badge ems-badge--off'}>
                {bulkSelected.size} selected
              </span>
            </div>

            <div className="ems-actionbar">
              <input
                className="lb-matrix-search"
                type="text"
                placeholder="Search employees by name or email"
                value={bulkSearch}
                onChange={(e) => setBulkSearch(e.target.value)}
              />
              <button type="button" className="ems-btn ems-btn--secondary" onClick={() => setBulkSelected(new Set(bulkVisible.map((e) => e.email)))} disabled={bulkLoading || bulkVisible.length === 0}>
                Select all ({bulkVisible.length})
              </button>
              <button type="button" className="ems-btn ems-btn--secondary" onClick={() => setBulkSelected(new Set())} disabled={bulkSelected.size === 0}>
                Clear
              </button>
            </div>

            {bulkLoading ? (
              <p className="ems-empty">Loading employees…</p>
            ) : bulkVisible.length === 0 ? (
              <p className="ems-empty">No employees match your search.</p>
            ) : (
              <div className="lb-bulk-emplist">
                {bulkVisible.map((e) => (
                  <label key={e.email} className={bulkSelected.has(e.email) ? 'lb-bulk-emprow selected' : 'lb-bulk-emprow'}>
                    <input type="checkbox" checked={bulkSelected.has(e.email)} onChange={() => toggleBulk(e.email)} />
                    <span>{e.fullName || e.name}</span>
                    <span className="lb-bulk-empmeta">{e.department || '—'}</span>
                  </label>
                ))}
              </div>
            )}

            <div className="ems-form-actions">
              <button type="button" className="ems-btn-primary" onClick={handleBulkApply} disabled={bulkBusy || bulkSelected.size === 0}>
                {bulkBusy ? 'Applying…' : 'Apply to ' + bulkSelected.size + ' employee(s)'}
              </button>
            </div>
          </div>
        </>
      )}

      {activeTab === 'ROLLOVER' && (
        <div className="ems-card">
          <div className="ems-card-head">
            <div>
              <h3>Year-End Processing</h3>
              <p className="ems-card-sub">
                Resets balances for the new year. This company does not use carry-forward, so unused
                leave lapses and new-year balances accrue fresh from each employee's date of joining.
              </p>
            </div>
            <span className="ems-badge ems-badge--warn">Irreversible</span>
          </div>

          <div className="ems-form-actions">
            <button type="button" className="ems-btn ems-btn--warn" onClick={handleRolloverSubmit}>
              Execute {year} to {year + 1} rollover
            </button>
          </div>
        </div>
      )}

      {activeTab === 'SETTINGS' && (
        <div className="ems-card">
          <div className="ems-card-head">
            <div>
              <h3>Leave Settings</h3>
              <p className="ems-card-sub">Organisation-wide rules, applied to every employee and leave type.</p>
            </div>
          </div>

          <div className="ems-form-grid">
            <div className="ems-field">
              <label htmlFor="lock-period">Lock period (days)</label>
              <input
                id="lock-period"
                type="number"
                min={0}
                max={90}
                value={settingsDraft.lockPeriodDays}
                onChange={(e) => setSettingsDraft(prev => ({ ...prev, lockPeriodDays: Number(e.target.value) }))}
              />
              <span className="ems-field-hint">
                How long employees may still edit or delete a request <strong>after</strong> the leave
                has started. They can always change it up to the day before it begins; this extends
                that window. Once it closes, only HR and Super Admin can make changes. It does
                <strong> not</strong> limit which dates employees may apply for.
              </span>
            </div>

            <div className="ems-field">
              <label>Current effect</label>
              {/* Worked through the same helper the permission check uses, so the example
                  cannot drift from the actual rule. */}
              <span className="ems-badge ems-badge--info ems-badge-standalone">
                {settingsDraft.lockPeriodDays === 0 ? 'Day before only' : 'Plus ' + settingsDraft.lockPeriodDays + ' day(s) after start'}
              </span>
              <span className="ems-field-hint">
                {settingsDraft.lockPeriodDays === 0
                  ? 'A leave starting 2026-08-20 stays editable up to 2026-08-19, and not on the 20th itself.'
                  : 'A leave starting 2026-08-20 stays editable until ' + employeeChangeDeadline('2026-08-20', settingsDraft.lockPeriodDays) + ' (exclusive).'}
              </span>
            </div>

            <div className="ems-field ems-field--wide">
              <label className="ems-inline-check">
                <input
                  type="checkbox"
                  checked={settingsDraft.allowEmployeeDeletion}
                  onChange={(e) => setSettingsDraft(prev => ({ ...prev, allowEmployeeDeletion: e.target.checked }))}
                />
                Let employees edit and delete their own requests
              </label>
              <span className="ems-field-hint">
                Subject to the lock period above. Managers cannot edit or delete requests at all —
                they approve or reject, which leaves a decision on record where an edit would leave
                nothing.
              </span>
            </div>
          </div>

          <div className="ems-form-actions">
            <button
              type="button"
              className="ems-btn-primary"
              onClick={() => {
                const saved = LeavePolicyService.saveSettings(settingsDraft);
                setSettingsDraft(saved);
                showToast('Leave settings saved. They take effect immediately for every employee.', 'success');
              }}
            >
              Save Settings
            </button>
            <button type="button" className="ems-btn ems-btn--secondary" onClick={() => setSettingsDraft(LeavePolicyService.getSettings())}>
              Reset
            </button>
          </div>
        </div>
      )}

      {activeTab === 'AUDIT' && (
        auditLogs.length === 0 ? (
          <div className="ems-card">
            <p className="ems-empty">
              No audit entries yet. Adjustments, comp-off grants and rollovers all appear here.
            </p>
          </div>
        ) : (
          <div className="ems-card ems-card--table">
            <div className="ems-table-scroll">
              <table className="ems-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Employee</th>
                    <th>Action</th>
                    <th>Change</th>
                    <th>Changed By</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => (
                    <tr key={log.id}>
                      <td>{new Date(log.timestamp).toLocaleString()}</td>
                      <td>{log.employeeName || nameForEmail(log.employeeEmail)}</td>
                      <td>{log.action}</td>
                      <td>{log.previousValue != null ? log.previousValue + ' → ' + (log.newValue ?? '') : (log.newValue ?? '')}</td>
                      <td>
                        {(() => {
                          const actor = actorFor(log.changedBy);
                          return (
                            <span className="hr-actor">
                              <span className="hr-actor-name">{actor.name}</span>
                              {actor.email && actor.email !== actor.name && (
                                <span className="hr-actor-email">{actor.email}</span>
                              )}
                              {actor.role && <span className="hr-actor-role">{actor.role}</span>}
                            </span>
                          );
                        })()}
                      </td>
                      <td>{log.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}
    </section>
  );
};

export default HROperationsPage;
